"use client";
import React, { useState, useRef, useEffect, useMemo } from "react";
import { Images } from "@assets/assets";
import Image, { StaticImageData } from "next/image";

type StepItem = {
  title: string;
  video?: string;
  image?: StaticImageData | string;
  description: string;
};

type IndustryApproachHeader = {
  title: React.ReactNode;
  subtitle: string;
  paragraph?: string;
  dark?: boolean;
};

interface IndustryApproachProps {
  header?: IndustryApproachHeader;
  steps?: StepItem[];
  version?: "dark" | "light";
}

export default function IndustryApproach({
  header,
  steps,
  version,
}: IndustryApproachProps) {
  const isDark = (version ?? "dark") === "dark";
  const stepsData = useMemo<StepItem[]>(() => steps ?? [], [steps]);
  const steplist = useMemo(() => stepsData?.map((s) => s.title), [stepsData]);
  const dotsPerStep = 8;
  const dotsArray = useMemo(
    () => Array.from({ length: stepsData.length * dotsPerStep }),
    [stepsData.length],
  );
  const [currentStep, setCurrentStep] = useState(0);

  const navContainerRef = useRef<HTMLDivElement | null>(null);
  const stepNavRef = useRef<HTMLDivElement | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!stepsData.length) return;
    const isDesktop = window.innerWidth >= 1024;
    if (!isDesktop) return;

    const stepNav = stepNavRef.current;
    const navContainer = navContainerRef.current;
    if (!stepNav || !navContainer) return;

    let ctx: any;
    let cancelled = false;

    (async () => {
      const [gsapModule, ScrollTriggerModule] = await Promise.all([
        import("gsap"),
        import("gsap/ScrollTrigger"),
      ]);
      if (cancelled) return;

      const gsap = gsapModule.default;
      const ScrollTrigger =
        ScrollTriggerModule.default ||
        ScrollTriggerModule.ScrollTrigger ||
        ScrollTriggerModule;
      gsap.registerPlugin(ScrollTrigger);

      ctx = gsap.context(() => {
        const travelY = stepNav.scrollHeight - navContainer.offsetHeight;

        // ✅ Add will-change only when scroll animation is active
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top top",
            end: "+=3000",
            scrub: 1,
            pin: true,
            // This section sits BELOW other lazy-loaded pinned sections
            // (WhyKonstant, IndustryProcess). They each register their pin at a
            // different time, so ScrollTrigger can measure this one before the
            // spacers above it exist — caching a start ~10000px too high and
            // leaving a full-screen white gap where the pin should be. A lower
            // refreshPriority forces this trigger to recompute LAST, after the
            // higher pins have applied their spacing, so its start is correct.
            refreshPriority: -1,
            invalidateOnRefresh: true,
            // ✅ Promote layer only during active scroll
            onEnter: () => {
              stepNav.style.willChange = "transform";
            },
            onLeave: () => {
              stepNav.style.willChange = "auto";
            },
            onEnterBack: () => {
              stepNav.style.willChange = "transform";
            },
            onLeaveBack: () => {
              stepNav.style.willChange = "auto";
            },
            onUpdate: (self: any) => {
              const activeIndex = Math.min(
                Math.floor(self.progress * stepsData.length),
                stepsData.length - 1,
              );
              setCurrentStep(activeIndex);
            },
          },
        });

        tl.to(stepNav, { y: -travelY, ease: "none" }, 0);
      });
    })();

    return () => {
      cancelled = true;
      ctx?.revert?.();
    };
  }, [stepsData]);

  return (
    <div className="w-full bg-transparent" suppressHydrationWarning>
      {/* ── Mobile layout (unchanged) ── */}
      <div
        className={`block min-[1300px]:hidden px-[5%] py-12 ${
          isDark ? "bg-black" : "bg-white"
        }`}
      >
        <span
          className={`uppercase tracking-widest text-[10px] mb-3 block font-medium ${
            isDark ? "text-white/60" : "text-black/60"
          }`}
        >
          {header?.subtitle}
        </span>
        <h2
          className={`clash-font text-3xl mb-10 lg:mb-6 font-medium ${
            isDark ? "text-white" : "text-black"
          }`}
        >
          {header?.title}
        </h2>
        <div className="flex flex-col gap-12">
          {stepsData?.map((step, index) => (
            <div key={index} className="flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-[2px] bg-blue-500" />
                <h3
                  className={`clash-font text-2xl lg:text-3xl mb-0 lg:mb-3 font-medium ${isDark ? "text-white" : "text-black"}`}
                >
                  {step.title}
                </h3>
              </div>
              {/* Image renders identically on server + client (visibility is
                  CSS-only: hidden on phones, shown from md up). Previously gated
                  on `typeof window` which made the server skip it and the client
                  render it → structural hydration mismatch (React #418). */}
              {step.image && (
                <div className="hidden md:block relative w-full aspect-video rounded-xl overflow-hidden bg-white/5">
                  <Image
                    src={step.image as any}
                    alt={step.title}
                    fill
                    className="object-cover"
                    sizes="(max-width: 1024px) 100vw, 50vw"
                    loading="lazy"
                  />
                </div>
              )}
              <p
                className={`text-sm lg:text-base leading-relaxed ${
                  isDark ? "text-white/60" : "text-black/60"
                }`}
                dangerouslySetInnerHTML={{ __html: step.description ?? "" }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* ── Desktop layout ── */}
      <section
        ref={sectionRef}
        // NOTE: no `sticky top-0` here — GSAP ScrollTrigger `pin: true` owns the
        // pinning. Having both made CSS sticky fight GSAP's pin-spacer: the
        // spacer reserved ~4080px of scroll but the section parked below the
        // viewport for most of it, leaving a full-screen white gap.
        className={`hidden xl:block industry-approach ${
          isDark ? "bg-black" : "bg-white"
        } w-full overflow-hidden h-screen`}
        suppressHydrationWarning
      >
        <div
          className={`texture-bg absolute inset-0 z-0 ${
            isDark ? "mix-blend-overlay" : "opacity-10"
          }`}
        >
          <div className="absolute inset-0 z-0">
            <Image
              src={Images?.texture}
              alt=""
              aria-hidden="true"
              fill
              className="object-cover mix-blend-overlay opacity-60"
              // ✅ Decorative image — not priority
              loading="lazy"
              sizes="100vw"
            />
          </div>
        </div>

        <div className="max-w-[2500px] h-full mx-auto w-[90%] flex flex-col py-10 relative z-10">
          <div className="shrink-0 mb-[60px]">
            <span
              className={`uppercase tracking-widest text-xs mb-3 block font-medium ${
                isDark ? "text-white/60" : "text-black/60"
              }`}
            >
              {header?.subtitle}
            </span>
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-end gap-6 mt-6">
              <div className="lg:w-7/12">
                <h2
                  className={`clash-font text-4xl lg:text-6xl leading-tight font-medium ${
                    isDark ? "text-white" : "text-black"
                  }`}
                >
                  {header?.title}
                </h2>
              </div>
              <div className="lg:w-4/12">
                <p
                  className={`text-base lg:text-lg leading-relaxed font-light ${
                    isDark ? "text-white/70" : "text-black/70"
                  }`}
                  dangerouslySetInnerHTML={{ __html: header?.paragraph ?? "" }}
                />
              </div>
            </div>
          </div>

          <div className="flex gap-8 flex-1 overflow-hidden items-stretch">
            {/* ── Step nav dots ── */}
            <div
              ref={navContainerRef}
              className="shrink-0 min-w-[300px] relative overflow-hidden h-full
                [mask-image:linear-gradient(to_bottom,transparent,black_15%,black_85%,transparent)]"
            >
              {/* ✅ will-change removed from className — set dynamically via JS */}
              <div
                ref={stepNavRef}
                className="flex flex-col gap-4 pt-[10vh] pb-[40vh]"
              >
                {dotsArray?.map((_, index) => {
                  const stepIndex = Math.floor(index / dotsPerStep);
                  const isActive = stepIndex === currentStep;
                  const isStepHeader = index % dotsPerStep === 0;

                  const dotColor = isActive
                    ? "bg-blue-500"
                    : isDark
                      ? "bg-white/20"
                      : "bg-blue-500";

                  const lineColor = isActive
                    ? "bg-blue-500"
                    : isDark
                      ? "bg-white/20"
                      : "bg-black/20";

                  const textColor = isActive
                    ? isDark
                      ? "text-white font-bold"
                      : "text-black font-bold"
                    : isDark
                      ? "text-white/70"
                      : "text-black/70";

                  return (
                    <div
                      key={index}
                      className={`w-1 h-1 rounded-full transition-colors duration-300 ${dotColor}`}
                    >
                      {isStepHeader && (
                        <div className="flex items-center gap-3 absolute left-0 translate-y-[-50%]">
                          <div
                            className={`w-6 h-1 rounded-full transition-colors duration-300 ${lineColor}`}
                          />
                          <span
                            className={`whitespace-nowrap text-lg transition-all duration-300 ${textColor}`}
                          >
                            {steplist[stepIndex]}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── Step image + description ── */}
            <div className="flex-1 relative flex items-center justify-center min-h-0">
              <div className="relative w-full h-full max-h-[800px] flex items-center justify-start">
                <div className="relative w-[75%] h-full">
                  {stepsData[currentStep]?.image && (
                    <div
                      key={`image-${currentStep}`}
                      className="absolute inset-0 transition-opacity duration-700 ease-in-out opacity-100 z-10"
                    >
                      <Image
                        src={stepsData[currentStep].image as any}
                        alt={`Step ${currentStep + 1}: ${stepsData[currentStep].title}`}
                        className="rounded-xl object-contain w-full h-full"
                        width={1200}
                        height={800}
                        // Below-the-fold process image — lazy load, never priority
                        loading="lazy"
                        sizes="(max-width: 1024px) 100vw, 50vw"
                        fetchPriority="auto"
                      />
                    </div>
                  )}
                </div>

                <div
                  className={`${
                    isDark
                      ? "bg-[#0E131E] text-white"
                      : "bg-[#ECF0F6] text-black"
                  } absolute right-0 z-20 bottom-4 lg:bottom-0 p-8 w-[55%] md:min-h-[225px] border ${
                    isDark ? "border-white/10" : "border-black/5"
                  }`}
                >
                  <div className="relative">
                    {stepsData?.map((step, index) => (
                      <div
                        key={`text-${index}`}
                        className={`transition-all duration-500 ${
                          index === currentStep
                            ? "opacity-100 translate-y-0"
                            : "opacity-0 absolute inset-0 translate-y-2 pointer-events-none"
                        }`}
                      >
                        <h3 className="clash-font text-2xl lg:text-3xl mb-0 lg:mb-3  font-medium">
                          {step.title}
                        </h3>
                        <p
                          className={`text-sm lg:text-base leading-relaxed ${
                            isDark ? "text-white/60" : "text-black/60"
                          }`}
                          dangerouslySetInnerHTML={{
                            __html: step.description ?? "",
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Side progress dots ── */}
            <div className="shrink-0 flex flex-col justify-center gap-4">
              {stepsData?.map((step, index) => (
                <div
                  key={`nav-dot-${index}`}
                  className={`rounded-full transition-all duration-300 ${
                    index === currentStep
                      ? "w-3 h-3 bg-blue-500"
                      : `w-2 h-2 ${isDark ? "bg-white/20" : "bg-black/20"}`
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
